import { useCallback, useEffect, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { ListSelect } from '@bambi/component-library/src/components/ListSelect/ListSelect';
import { CheckIcon, TruckIcon, UserIcon } from '@heroicons/react/24/outline';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { Modal } from 'src/common/primitives/Modal';
import { ModalTitle } from 'src/common/primitives/Modal/ModalTitle';
import { formatFullName } from 'src/common/util/formatFullName';

import { AttendantProfileForm } from '../attendant/AttendantProfileForm/AttendantProfileForm';
import { DriverProfileForm } from '../DriverProfileForm/DriverProfileForm';
import {
  setShowAttendantProfileModal,
  setShowDriverProfileModal,
  setShowMemberProfilePromptModal,
} from '../team.slice';

type NextStepOption = 'driver-attendant' | 'attendant-only';

export function MemberProfilePromptModal() {
  const dispatch = useAppDispatch();
  const [value, setValue] = useState<NextStepOption>('driver-attendant');

  const open = useAppSelector(
    (state) => state.team.showMemberProfilePromptModal
  );
  const member = useAppSelector((state) => state.team.selectedMemberProfile);

  const handleClose = useCallback(() => {
    dispatch(setShowMemberProfilePromptModal({ open: false, member: null }));
  }, [dispatch]);
  const handleOpenNext = useCallback(() => {
    if (value === 'driver-attendant') {
      dispatch(setShowDriverProfileModal(true));
    } else {
      dispatch(setShowAttendantProfileModal(true));
    }
  }, [dispatch, value]);

  // Reset value on open change
  useEffect(() => {
    setValue('driver-attendant');
  }, [open]);

  if (!member) {
    return null;
  }

  return (
    <>
      <Modal
        title="Add driver or attendant profile for new member?"
        open={open}
        setOpen={(open) => {
          if (!open) {
            handleClose();
          }
        }}
      >
        <div className="flex max-w-sm flex-col gap-4">
          <div className="flex flex-col gap-2 px-2 text-center">
            <div className="flex justify-center">
              <div
                className={`flex h-[48px] w-[48px] items-center justify-center rounded-full border-8 border-success-200 bg-success-50 text-mint`}
              >
                <CheckIcon
                  className="w-5"
                  data-testid="confirm-modal-success-icon"
                />
              </div>
            </div>

            <ModalTitle>{formatFullName(member)} has been added</ModalTitle>
            <div className="text-sm text-gray-500">
              Would you like to set up a profile for this member? You can always
              do this later.
            </div>
          </div>

          <ListSelect
            value={value}
            items={[
              {
                title: 'Driver + Attendant',
                description:
                  'Can be assigned to drive vehicles and attend to passengers.',
                value: 'driver-attendant',
                icon: <TruckIcon className="h-8 w-8 stroke-mint" />,
              },
              {
                title: 'Attendant only',
                description:
                  'Can be assigned to attend to passengers but not drive.',
                value: 'attendant-only',
                icon: <UserIcon className="h-8 w-8 stroke-mint" />,
              },
            ]}
            onChange={setValue}
          />
          <div className="flex flex-row gap-2">
            <Button
              variant="secondary"
              className="w-full"
              onClick={handleClose}
            >
              Skip for now
            </Button>
            <Button
              variant="primary"
              className="w-full"
              onClick={handleOpenNext}
            >
              Continue
            </Button>
          </div>
          <div className="text-center text-xs text-gray-500">
            You can add or change profiles anytime from the member's detail
            page.
          </div>
        </div>
      </Modal>
      <DriverProfileForm />
      <AttendantProfileForm />
    </>
  );
}
